"use client";

import { HttpLink, from } from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { onError } from "@apollo/client/link/error";
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
} from "@apollo/client-integration-nextjs";
import { Auth0Provider, useUser } from "@auth0/nextjs-auth0/client";
import type { ExperimentResult } from "@sa-nu/experiments/bucket";
import { ExperimentProvider } from "@sa-nu/experiments/experiment-context";
import {
  datadogBrowserLogger,
  logger,
  setBrowserLogContext,
} from "@sa-nu/logger";
import {
  GlobalGraphQLErrors,
  GlobalGraphQLErrorSubTypes,
} from "@sa-nu/shared-types";
import { usePathname, useSearchParams } from "next/navigation";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import type { ReactNode } from "react";
import { useCallback, useEffect } from "react";

import { initClientTimeOverride } from "../../constantsClient";
import { isPublicProductionEnv } from "../../constantsClient";
import {
  screenViewIds,
  screenViewKey,
} from "../../lib/analytics/screen-view-ids";
import { localeLink } from "../../lib/apollo/locale-link";
import {
  hasGraphQLErrorCode,
  suppressesUnauthenticatedAlert,
} from "../../lib/apollo/unauthenticated";
import GtmUserIdTracker from "../../lib/google-tag-manager/gtm-user-id-tracker";
import { configureZodErrorMessages } from "../../lib/zod";

import { AlertDialogProvider, useAlertDialog } from "./alert-dialog";
import { NonceProvider } from "./nonce-context";
import { OpenFeatureProvider } from "./openfeature-provider";
import { SnackbarProvider } from "./snackbar";

// Configure Zod error messages globally (only runs once at module load)
configureZodErrorMessages();

// AbortControllerによるキャンセルかどうかを判定する。
// カレンダー日付ナビゲーションの連打対策で、古いリクエストを意図的にabortしている。
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

// layout.tsx が user-agent から data-client を立てている。use-in-native-app と違い
// error link のコールバックから同期的に読みたいので hook ではなく属性を直接見る
function isRenderedInNativeApp(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-client") === "nativeapp"
  );
}

// NOTE: @apollo/client@3.12.0 で型が壊れるようになったのでその対応
// Apollo の dataMasking 機能を使っているわけではないが、
// なぜか勝手に Query result の型を unmask してしまうようになったのでそれを抑制する
// We need to use interface for module augmentation
declare module "@apollo/client" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface DataMasking {
    enabled: true;
  }
}

export const RootProvider = ({
  nonce,
  visitorId,
  experiments,
  children,
}: {
  nonce: string | undefined;
  visitorId: string | undefined;
  experiments: ExperimentResult[];
  children: ReactNode;
}) => {
  // dev環境でのみ getCurrentTime() の時刻を上書き（子コンポーネントの描画前に設定）
  initClientTimeOverride();

  const searchParams = useSearchParams();
  if (searchParams.get("debug-log")) {
    logger.error("logger.error test");
  }
  if (searchParams.get("debug-throw")) {
    throw new Error("throw test");
  }

  return (
    <Auth0Provider>
      <NonceProvider nonce={nonce}>
        <ExperimentProvider visitorId={visitorId} experiments={experiments}>
          <NuqsAdapter>
            <OpenFeatureProvider>
              <AlertDialogProvider>
                <SnackbarProvider>
                  <ApolloInitializer nonce={nonce}>
                    <LogProvider />
                    <GtmUserIdTracker />
                    {children}
                  </ApolloInitializer>
                </SnackbarProvider>
              </AlertDialogProvider>
            </OpenFeatureProvider>
          </NuqsAdapter>
        </ExperimentProvider>
      </NonceProvider>
    </Auth0Provider>
  );
};

function ApolloInitializer({
  nonce,
  children,
}: {
  nonce: string | undefined;
  children: ReactNode;
}) {
  const showAlertDialog = useAlertDialog();

  const makeClient = useCallback(() => {
    const uri =
      typeof window !== "undefined" && window.location?.host
        ? `${window.location.protocol}//${window.location.host}/api/graphql`
        : "/api/graphql";

    const httpLink = new HttpLink({
      uri,
    });

    const errorLink = onError(({ error, result, operation }) => {
      // AbortControllerによる意図的なキャンセルはエラーとして扱わない
      if (isAbortError(error)) return;

      const requestId = result?.extensions?.["requestId"];
      const logger = datadogBrowserLogger();

      const isGraphQLError = CombinedGraphQLErrors.is(error);

      // Redirect to explanation page when user tries to social-login
      // with an email that has an existing unverified account
      if (
        hasGraphQLErrorCode(
          error,
          GlobalGraphQLErrorSubTypes.UNAUTHENTICATED.UNVERIFIED_EMAIL_CONFLICT,
        )
      ) {
        window.location.href = "/auth/verify-existing-email";
        return;
      }

      // Redirect when social identity was not linked to an auth0| account
      // (user skipped account linking or post-login action failed)
      if (
        hasGraphQLErrorCode(
          error,
          GlobalGraphQLErrorSubTypes.UNAUTHENTICATED.SOCIAL_IDENTITY_NOT_LINKED,
        )
      ) {
        window.location.href = "/login?error=SOCIAL_IDENTITY_NOT_LINKED";
        return;
      }

      if (
        hasGraphQLErrorCode(
          error,
          GlobalGraphQLErrorSubTypes.UNAUTHENTICATED.DELETED_USER_SESSION,
        )
      ) {
        // webview では /auth/logout に飛ばしても native app 側のセッションは残る。
        // AccountDelete を postMessage する /webview-expired を経由させて logout() させる
        window.location.href = isRenderedInNativeApp()
          ? "/webview-expired?reason=deleted"
          : "/auth/logout";
        return;
      }

      if (hasGraphQLErrorCode(error, GlobalGraphQLErrors.UNAUTHENTICATED)) {
        if (suppressesUnauthenticatedAlert(operation.getContext())) return;
        showAlertDialog({
          title: "ログインが必要です",
          normalBody: "お手数ですが、再度ログインしてからやり直してください。",
        });
        return;
      }

      if (logger) {
        if (isGraphQLError) {
          logger.error(`[GraphQL error]: ${operation.operationName}`, {
            requestId,
            gqlOperation: operation,
            gqlError: error,
          });
        } else {
          logger.warn(`[Network error]: ${operation.operationName}`, {
            requestId,
            gqlOperation: operation,
            gqlError: error,
          });
        }
      }

      const dialogContent = {
        title: isGraphQLError
          ? "エラーが発生しました"
          : "ネットワークエラーが発生しました",
        mediumBody: isGraphQLError
          ? "画面を更新して、再度お試しください"
          : "しばらくしてから再度お試しください",
      };
      const normalBody = [
        "問題が解決しない場合は、お手数ですがお問い合わせください。",
      ];
      if (requestId) {
        normalBody.push(`お問い合わせID: ${requestId}`);
      }
      if (!isPublicProductionEnv()) {
        normalBody.push(JSON.stringify(error, undefined, 2));
      }
      showAlertDialog({
        ...dialogContent,
        normalBody: normalBody.join("\n\n"),
      });
    });

    // localeLink: window.location.pathname の locale (例: /en/...) を `x-language` ヘッダーに乗せる。
    // BE (Yoga) 側は `caller.locale` にセットして resolver の locale 解決に使う。
    const link = from([errorLink, localeLink, httpLink]);

    return new ApolloClient({
      link,
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showAlertDialogをuseCallbackで安定化しているため依存配列から除外
  }, []);

  return (
    <ApolloNextAppProvider makeClient={makeClient} extraScriptProps={{ nonce }}>
      {children}
    </ApolloNextAppProvider>
  );
}

function LogProvider() {
  const { user } = useUser();
  useEffect(() => {
    if (user == null) return;

    setBrowserLogContext({
      auth0UserId: user.sub ?? null,
    });
  }, [user]);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => {
    setBrowserLogContext({
      url: window.location.href,
      urlPath: pathname,
      urlQuery: searchParams.toString(),
      // イベントログの screen_view_id と同じ値。ログとクリックを表示単位で join する
      screenSessionId: screenViewIds.forView(
        screenViewKey(pathname, searchParams.toString()),
      ),
    });
  }, [pathname, searchParams]);

  return null;
}
